import "./emailAutomaticTermsBanner.scss";
import React, { useEffect, useMemo, useState } from "react";
import {
  CampaignOptions,
  CampaignCookieOptions,
  ICONS_DEFAULT_VALUES,
} from "../../../../types/global.types";
import { getStorageItem, updateStorageItem } from "../../../../utility/storage";
import TemplateIcon from "../Icons/info.component";
import { HEADER_EMAIL_AUTOMATIC_TERMS_TEXT_AUTO_SEND } from "../../utils/emailAutomaticTerms.utils";

interface Props {
  campaignGeneralOptions?: CampaignOptions;
  cookieOptions?: CampaignCookieOptions;
  text?: string;
  email?: string;
  icon?: string;
  mobile?: boolean;
  onEmailChange?: (email: string) => void;
  wrap?: boolean;
  compact?: boolean;
}

const EMAIL_STORAGE_KEYS = ["brswEmail"];
const DEFAULT_COOKIE_OPTIONS: CampaignCookieOptions = {
  allowCookies: false,
  allowSessionCookies: false,
  allowPersistentCookies: false,
  expiryDays: "1000",
};

const readStoredEmail = (options: CampaignCookieOptions): string => {
  for (const key of EMAIL_STORAGE_KEYS) {
    const value = getStorageItem(key, options) || "";
    if (value) {
      return value;
    }
  }
  return "";
};

const EmailAutomaticTermsBanner = ({
  campaignGeneralOptions,
  cookieOptions,
  text,
  email,
  icon,
  onEmailChange,
  mobile = false,
  wrap = false,
  compact = false,
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftEmail, setDraftEmail] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const storageOptions = useMemo(
    () => cookieOptions || DEFAULT_COOKIE_OPTIONS,
    [cookieOptions]
  );
  const resolvedEmail = useMemo(
    () =>
      currentEmail ||
      (email || readStoredEmail(storageOptions) || "").trim(),
    [currentEmail, email, storageOptions]
  );
  const isManualEmailEntryEnabled =
    campaignGeneralOptions?.manualEmailEntry === true;
  const isAutoSendEmailEnabled =
    campaignGeneralOptions?.autoSendEmail === true;
  const hasEmail = Boolean(resolvedEmail);

  const show =
    Boolean(campaignGeneralOptions?.emailCaptureOnOffers) &&
    (!isManualEmailEntryEnabled || isAutoSendEmailEnabled) &&
    hasEmail;

  useEffect(() => {
    setCurrentEmail((email || readStoredEmail(storageOptions) || "").trim());
  }, [email, storageOptions]);

  useEffect(() => {
    setDraftEmail(resolvedEmail);
  }, [resolvedEmail]);

  if (!show) {
    return null;
  }

  const headerEmailAutomaticTermsText =
    text?.trim() || HEADER_EMAIL_AUTOMATIC_TERMS_TEXT_AUTO_SEND;
  const headerEmailAutomaticTermsParts =
    headerEmailAutomaticTermsText.split("{email}");

  const persistEmail = (nextEmail: string) => {
    for (const key of EMAIL_STORAGE_KEYS) {
      updateStorageItem(key, nextEmail, storageOptions);
    }
  };

  const handleSaveEmail = () => {
    const nextEmail = draftEmail.trim();
    if (!nextEmail) {
      return;
    }
    setPreEmailStorage(nextEmail);
    setCurrentEmail(nextEmail);
    onEmailChange?.(nextEmail);
    setIsEditing(false);
  };

  const setPreEmailStorage = (email: string) => {
    window.top?.postMessage({ event: "setPreEmail", load: email }, "*");
  };


  return (
    mobile ? (
      <div className="super-mobile-email-automatic-terms-banner">
        <div className={`super-mobile-email-automatic-terms-banner-content ${wrap ? "wrap" : ""}`}>
          <span className="super-mobile-email-automatic-terms-banner-icon">
            <TemplateIcon icon={icon || ICONS_DEFAULT_VALUES.iconInfo} color="#161616" />
          </span>
          <span className="super-mobile-email-automatic-terms-banner-text">
            {headerEmailAutomaticTermsParts[0]}
            {resolvedEmail && <strong>{resolvedEmail}</strong>}
            {headerEmailAutomaticTermsParts[1] || ""}
            {(compact || mobile) ? (
              <>
                {" "}See BrandSwap's{" "}
                <a
                  href="https://brandswap.com/privacy/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Privacy Policy.
                </a>
              </>
            ) : (
              <>
                {" "}Your data will be processed according to BrandSwap’s{" "}
                <a
                  href="https://brandswap.com/privacy/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Privacy Policy.
                </a>
              </>
            )}
            {isEditing && (
              <div className="super-mobile-email-automatic-terms-banner-edit">
                <input
                  value={draftEmail}
                  onChange={(event) => setDraftEmail(event.target.value)}
                  className="super-mobile-email-automatic-terms-banner-input"
                />
                <button
                  type="button"
                  className="super-mobile-email-automatic-terms-banner-button"
                  onClick={handleSaveEmail}
                >
                  Save
                </button>
              </div>
            )}
          </span>
          {!isEditing && (
            <button
              type="button"
              className="super-mobile-email-automatic-terms-banner-button"
              onClick={() => setIsEditing(true)}
            >
              Change email
            </button>
          )}
        </div>

      </div>
    ) : (
      <div className={`super-desktop-email-automatic-terms-banner`}>
        <span className="super-desktop-email-automatic-terms-banner-icon">
          <TemplateIcon icon={icon || ICONS_DEFAULT_VALUES.iconInfo} color="#161616" />
        </span>
        <span className="super-desktop-email-automatic-terms-banner-text">
          {headerEmailAutomaticTermsParts[0]}
          {resolvedEmail && <strong>{resolvedEmail}</strong>}
          {headerEmailAutomaticTermsParts[1] || ""}
          {compact ? (
            <>
              {" "}See BrandSwap's{" "}
              <a
                href="https://brandswap.com/privacy/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy.
              </a>
            </>
          ) : (
            <>
              {" "}Your data will be processed according to BrandSwap’s{" "}
              <a
                href="https://brandswap.com/privacy/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy.
              </a>
            </>
          )}
        </span>
        {isEditing ? (
          <div className="super-desktop-email-automatic-terms-banner-edit">
            <input
              value={draftEmail}
              onChange={(event) => setDraftEmail(event.target.value)}
              className="super-desktop-email-automatic-terms-banner-input"
            />
            <button
              type="button"
              className="super-desktop-email-automatic-terms-banner-button"
              onClick={handleSaveEmail}
            >
              Save
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="super-desktop-email-automatic-terms-banner-button"
            onClick={() => setIsEditing(true)}
          >
            Change email
          </button>
        )}
      </div>
    )
  );
};

export default EmailAutomaticTermsBanner;
